import React from "react";
import AssignmentHeader from "./AssignmentHeader.jsx";
import TruckProtectionActionPanel from "./TruckProtectionActionPanel.jsx";
import TruckLoadProgressBar, { getTruckLoadProgressTone } from "./TruckLoadProgressBar.jsx";
import TripGroup from "./TripGroup.jsx";
import { joinClasses } from "./ResultsPrimitives.jsx";
import useFleetCardViewModel from "../hooks/useFleetCardViewModel.js";
import { findPortalPublicationRoute } from "../lib/portalPublicationsApi.js";

export {
  buildFleetSearchValue,
  buildOperationalStepReassignmentKey,
  formatTons,
  getAssignmentTripCount,
  getClientSelectionId,
  resolveFleetCardViewModel,
} from "../utils/resultsFleetCardViewModel.js";

function AssignmentMetricsBar({ shell }) {
  return (
    <div className="flex min-w-0 items-center gap-x-1 overflow-hidden whitespace-nowrap py-px text-[0.66rem] leading-4 text-[rgb(95_107_103)]">
      <span className="shrink-0">
        <span className="text-[0.6rem] font-extrabold uppercase tracking-[0.06em] text-[rgba(103,94,79,0.74)]">Cap. </span>
        <span className="font-semibold" data-assignment-capacity>{shell.capacityLabel}</span>
      </span>
      <span aria-hidden="true" className="shrink-0 text-slate-300">·</span>
      <span className="shrink-0">
        <span className="text-[0.6rem] font-extrabold uppercase tracking-[0.06em] text-[rgba(103,94,79,0.74)]">Pic </span>
        <span className="font-semibold" data-assignment-peak-load>{shell.peakLoadLabel}</span>
      </span>
      <span aria-hidden="true" className="shrink-0 text-slate-300">·</span>
      {shell.showCompartments ? (
        <span className="shrink-0 whitespace-nowrap">
          <span className="text-[0.6rem] font-extrabold uppercase tracking-[0.06em] text-[rgba(103,94,79,0.74)]">Comp.</span>
          <span className="font-semibold ml-1" data-assignment-compartments>{shell.compartmentsLabel}</span>
        </span>
      ) : null}
    </div>
  );
}

function formatTonnage(value) {
  if (value == null || Number.isNaN(Number(value))) return "0";
  return new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 1 }).format(value);
}

function CapacityProgressRow({ currentValueLabel, label, maxValueLabel, percentage }) {
  const safePercentage = Math.max(0, Math.min(percentage, 100));
  const tone = getTruckLoadProgressTone(percentage);

  return (
    <div className="grid gap-1">
      <div className="flex items-center justify-between gap-1.5 text-[0.66rem] leading-4.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="shrink-0 text-[0.6rem] font-medium text-slate-400">{label}</span>
          <span className={tone.textClassName}>{`${percentage}%`}</span>
        </div>
        <span className={tone.textClassName}>
          {currentValueLabel} / {maxValueLabel}
        </span>
      </div>

      <TruckLoadProgressBar percentage={safePercentage} />
    </div>
  );
}

function CapacityProgressSection({ assignment }) {
  const capacityTons = Number(assignment?.capacity_tons) || 0;
  const peakLoadTons = Number(assignment?.peak_trip_load_tons);
  const displayedLoadTons = Number.isFinite(peakLoadTons) && peakLoadTons > 0
    ? peakLoadTons
    : Math.max(Number(assignment?.total_quantity_tons) || 0, 0);

  if (capacityTons <= 0) return null;

  const weightPct = Math.min(Math.round((displayedLoadTons / capacityTons) * 100), 120);

  return (
    <div>
      <div className="flex flex-col gap-1">
        <CapacityProgressRow
          currentValueLabel={`${formatTonnage(displayedLoadTons)} t`}
          label="Poids"
          maxValueLabel={`${formatTonnage(capacityTons)} t`}
          percentage={weightPct}
        />
      </div>
    </div>
  );
}

export default function FleetCard(props) {
  const viewModel = useFleetCardViewModel(props);
  const interactions = props.interactions && typeof props.interactions === "object"
    ? props.interactions
    : {};
  const routeCanvasStepsPanel = props.routeCanvasStepsPanel === true;
  const [isTruckActionsOpen, setIsTruckActionsOpen] = React.useState(false);
  const isSelectedCard = viewModel.isSelectedAssignment;
  const isDispatchDisabledCard = Boolean(viewModel.shell.dispatchDisabledBadge);
  const isUnassignedCard = !isDispatchDisabledCard
    && (viewModel.shell.statusBadge.label === "Sans tournée" || viewModel.isIdle);
  const isErrorCard = viewModel.shell.statusBadge.tone === "warning" && !isUnassignedCard;
  const isHeuristicCard = viewModel.shell.statusBadge.label === "Heuristique seule" && !isUnassignedCard;
  const canShowTruckActions = typeof interactions.onToggleTruckDispatchDisabled === "function"
    || typeof interactions.onToggleTruckProtection === "function"
    || typeof interactions.onToggleTruckOrdersProtection === "function"
    || typeof interactions.onToggleTruckDompeurMixedLoad === "function"
    || typeof interactions.onToggleTruckOvertime === "function"
    || typeof interactions.onSetTruckPreloadTime === "function"
    || typeof interactions.onOptimizeTruckRoutes === "function"
    || typeof interactions.onPublishTruckPortal === "function"
    || typeof interactions.onUnpublishTruckPortal === "function";
  const canManageDriverPortalPublication = typeof interactions.onPublishTruckPortal === "function"
    || typeof interactions.onUnpublishTruckPortal === "function";
  const accentBarColor = isUnassignedCard
    ? "var(--color-danger)"
    : (isErrorCard || isHeuristicCard)
      ? "var(--color-amber)"
      : "var(--color-legacy-teal)";
  const resolvedAssignmentId = viewModel.assignmentId || viewModel.assignment?.truck_id || "";
  const portalPublicationRoute = canManageDriverPortalPublication
    ? findPortalPublicationRoute(
        interactions.portalPublications,
        viewModel.assignment?.truck_id || resolvedAssignmentId,
      )
    : null;
  const shouldUseRouteCanvasStepsPanel = routeCanvasStepsPanel
    && typeof window !== "undefined"
    && window.matchMedia("(min-width: 841px)").matches;

  React.useEffect(() => {
    setIsTruckActionsOpen(false);
  }, [viewModel.assignmentId]);

  const handleCloseTruckActions = React.useCallback(() => {
    setIsTruckActionsOpen(false);
  }, []);

  const handleOpenTruckActions = React.useCallback(() => {
    setIsTruckActionsOpen(true);
  }, []);

  const handleToggleTruckProtection = React.useCallback((payload = {}) => {
    setIsTruckActionsOpen(false);
    interactions.onToggleTruckProtection?.(payload);
  }, [interactions]);

  const handleToggleTruckDispatchDisabled = React.useCallback((payload = {}) => {
    setIsTruckActionsOpen(false);
    interactions.onToggleTruckDispatchDisabled?.(payload);
  }, [interactions]);

  const handleToggleTruckOrdersProtection = React.useCallback((payload = {}) => {
    setIsTruckActionsOpen(false);
    interactions.onToggleTruckOrdersProtection?.(payload);
  }, [interactions]);

  const handleToggleTruckOvertime = React.useCallback((payload = {}) => {
    setIsTruckActionsOpen(false);
    interactions.onToggleTruckOvertime?.(payload);
  }, [interactions]);

  const handleToggleTruckDompeurMixedLoad = React.useCallback((payload = {}) => {
    setIsTruckActionsOpen(false);
    interactions.onToggleTruckDompeurMixedLoad?.(payload);
  }, [interactions]);

  const handleSetTruckPreloadTime = React.useCallback((payload = {}) => {
    setIsTruckActionsOpen(false);
    interactions.onSetTruckPreloadTime?.(payload);
  }, [interactions]);

  const handleOptimizeTruckRoutes = React.useCallback((payload = {}) => {
    setIsTruckActionsOpen(false);
    interactions.onOptimizeTruckRoutes?.(payload);
  }, [interactions]);

  return (
    <article
      className={joinClasses([
        "relative isolate grid min-w-0 w-full overflow-hidden rounded-[4px] border px-1.5 py-1 text-[0.93rem] transition duration-150",
        "border-[rgba(148,163,184,0.16)] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06)]",
        isSelectedCard
          ? "border-border-accent-muted bg-white ring-1 ring-surface-accent-tint"
          : "hover:border-[rgba(148,163,184,0.24)]",
      ])}
      data-assignment-card
      data-assignment-id={viewModel.assignmentId}
      data-fleet-search={viewModel.fleetSearchValue}
      data-assignment-card-tone={isErrorCard ? "warning" : isUnassignedCard ? "unassigned" : "default"}
    >
      <div
        aria-hidden="true"
        className={joinClasses([
          "pointer-events-none absolute inset-y-0 left-0 rounded-l-[4px] transition-all duration-150",
          isSelectedCard ? "w-1" : "w-0.5",
        ])}
        style={{ background: isSelectedCard ? "var(--color-legacy-teal)" : accentBarColor }}
      />
      <div className="mb-0.5">
        <AssignmentHeader
          isOpen={viewModel.isOpen}
          isSelected={isSelectedCard}
          isTruckActionsOpen={isTruckActionsOpen}
          onCloseTruckActions={handleCloseTruckActions}
          onOpenTruckActions={canShowTruckActions ? handleOpenTruckActions : null}
          onSelect={() => {
            interactions.onSelectAssignment?.(resolvedAssignmentId);
            if (shouldUseRouteCanvasStepsPanel && !viewModel.isOpen) {
              interactions.onToggleAssignment?.(resolvedAssignmentId);
            }
          }}
          onToggle={() => {
            if (shouldUseRouteCanvasStepsPanel) {
              interactions.onSelectAssignment?.(resolvedAssignmentId);
            }
            interactions.onToggleAssignment?.(resolvedAssignmentId);
          }}
          portalPublication={portalPublicationRoute}
          shell={viewModel.shell}
        />
      </div>

      {isTruckActionsOpen && canShowTruckActions ? (
        <div className="bg-white px-2 pb-1">
          <TruckProtectionActionPanel
            assignment={viewModel.assignment}
            compact
            onClose={handleCloseTruckActions}
            onOptimizeTruckRoutes={handleOptimizeTruckRoutes}
            onPublishTruckPortal={typeof interactions.onPublishTruckPortal === "function"
              ? (route) => {
                  setIsTruckActionsOpen(false);
                  interactions.onPublishTruckPortal(route);
                }
              : null}
            onSetTruckPreloadTime={handleSetTruckPreloadTime}
            onToggleTruckDispatchDisabled={handleToggleTruckDispatchDisabled}
            onToggleTruckDompeurMixedLoad={handleToggleTruckDompeurMixedLoad}
            onToggleTruckOrdersProtection={handleToggleTruckOrdersProtection}
            onToggleTruckOvertime={handleToggleTruckOvertime}
            onToggleTruckProtection={handleToggleTruckProtection}
            onUnpublishTruckPortal={typeof interactions.onUnpublishTruckPortal === "function"
              ? (route) => {
                  setIsTruckActionsOpen(false);
                  interactions.onUnpublishTruckPortal(route);
                }
              : null}
            portalPublications={interactions.portalPublications}
          />
        </div>
      ) : null}

      <div className="bg-white px-2">
        <AssignmentMetricsBar shell={viewModel.shell} />
      </div>

      <div className="bg-white px-2 pb-1.5">
        <CapacityProgressSection assignment={viewModel.assignment} />
      </div>

      {viewModel.isOpen ? (
        <div
          className={joinClasses([
            "grid min-w-0 gap-1 rounded-[4px] bg-white transition duration-150",
            routeCanvasStepsPanel ? "min-[841px]:hidden" : "",
          ])}
          data-assignment-body
        >
          {viewModel.tripGroups.map((group) => (
            <TripGroup
              key={group.tripId}
              group={group}
              interactions={interactions}
            />
          ))}
        </div>
      ) : null}
    </article>
  );
}
